import { useState, useEffect } from "react";

import axios from "axios";
import { DotsSixVertical, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const DEFAULT_COLUMNS = [
  { key: "nr", label: "Nr.", visible: true },
  { key: "vorname", label: "Vorname", visible: true },
  { key: "nachname", label: "Nachname", visible: true },
  { key: "geburtsdatum", label: "Geburtsdatum", visible: true },
  { key: "geschlecht", label: "Geschlecht", visible: true },
  { key: "spieler_lizenz", label: "Spieler-Lizenz", visible: true },
  { key: "schiedsrichter_lizenz", label: "Schiedsrichter-Lizenz", visible: true },
  { key: "gueltig_ab", label: "Gültig ab", visible: true },
  { key: "gueltig_bis", label: "Gültig bis", visible: true },
  { key: "aktiv", label: "Aktiv", visible: true },
  { key: "mannschaft", label: "Mannschaft", visible: true },
  { key: "lehrgang", label: "geplant", visible: true },
];

export function ColumnConfigDialog({ open, onOpenChange, sessionId, columnConfig, onSuccess, API }) {
  const [columns, setColumns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (columnConfig && columnConfig.length > 0) {
      // Filter out 'actions' - it's auto-appended by the table, never saved
      setColumns(columnConfig.filter(c => c.key !== 'actions'));
    }
  }, [columnConfig, open]);

  const handleVisibilityToggle = (index) => {
    const newColumns = [...columns];
    // Don't allow hiding Nr. column
    if (newColumns[index].key === "nr") return;
    newColumns[index] = { ...newColumns[index], visible: !newColumns[index].visible };
    setColumns(newColumns);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newColumns = [...columns];
    const draggedItem = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(index, 0, draggedItem);
    setColumns(newColumns);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Never save the 'actions' column
      const toSave = columns.filter(c => c.key !== 'actions');
      await axios.put(`${API}/columns?session_id=${sessionId}`, { columns: toSave });
      
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setColumns(DEFAULT_COLUMNS);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">
            Spalten konfigurieren
          </DialogTitle>
          <DialogDescription>
            Spalten per Drag & Drop verschieben und ein-/ausblenden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-4 max-h-[400px] overflow-y-auto">
          {columns.map((col, index) => (
            <div
              key={col.key}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-2 rounded border border-border bg-card cursor-move ${draggedIndex === index ? 'opacity-50' : ''}`}
            >
              <DotsSixVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 text-sm">{col.label}</span>
              <button
                onClick={() => handleVisibilityToggle(index)}
                className={`p-1 rounded ${col.visible ? 'text-primary' : 'text-muted-foreground'}`}
                disabled={col.key === "nr"}
                title={col.visible ? "Ausblenden" : "Einblenden"}
              >
                {col.visible ? (
                  <Eye className="h-4 w-4" />
                ) : (
                  <EyeSlash className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1"
          >
            Zurücksetzen
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
          >
            {saving ? "Speichern..." : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
